import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Button, message } from 'antd';
import { CheckoutData } from '.';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import ConfettiExplosion from 'react-confetti-explosion';
import { useDispatch } from 'react-redux';
import { setProjectRetainerBudget } from 'src/store/projectRetainer/reducer';
import { formatCurrency } from 'src/utils/string';

interface PaymentFormProps {
  onClose: (clearCart?: boolean) => void;
  checkoutData: CheckoutData;
}

function PaymentForm({ onClose, checkoutData }: PaymentFormProps) {
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [successfulPayment, setSuccessfulPayment] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    setIsProcessing(true);

    try {
      if (!checkoutData.client_secret) {
        message.error('Payment setup failed. Please try again.');
        return;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        checkoutData.client_secret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (error) {
        message.error(`Payment failed: ${error.message}`);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        message.success('Payment successful!');

        setSuccessfulPayment(true);

        await apiRequests.post(`${apiRoutes.CHECKOUT_SUCCESS}`, {
          qty: checkoutData.qty,
          project_uuid: checkoutData.project_id,
          payment_intent: checkoutData.id,
        });

        dispatch(setProjectRetainerBudget(checkoutData.qty * 60));

        onClose(true);
      }
    } catch {
      message.error('An unexpected error occurred.');
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
          }}
        >
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            Card details
          </label>
          <CardElement options={cardElementOptions} />
        </div>

        <Button
          type="primary"
          htmlType="submit"
          disabled={!stripe}
          loading={isProcessing}
          style={{
            border: 'none',
            padding: '15px',
            borderRadius: '6px',
            fontSize: '16px',
            width: '100%',
            height: '48px',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
          }}
        >
          {isProcessing
            ? 'Processing...'
            : `Pay ${formatCurrency(checkoutData.amount / 100)}`}
        </Button>
      </form>

      {successfulPayment && (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
          <ConfettiExplosion
            force={0.8}
            duration={3000}
            particleCount={300}
            width={1600}
            zIndex={99999}
            colors={['#041E43', '#1471BF', '#5BB4DC', '#FC027B', '#66D805']}
          />
        </div>
      )}
    </>
  );
}

export default PaymentForm;
